import useSWR from 'swr';

import { useFsWatch } from '@/api-models/sandbox-fs-watch';
import { fetchEndpointData } from '@/utils/fetch.client';

export interface FileTreeNode {
  name: string;
  path: string;
  is_dir: boolean;
  children?: FileTreeNode[];
  lazy?: boolean;
}

interface FileTreeResponse {
  tree: FileTreeNode[];
  workspace_root: string;
  clock: number;
}

export interface FileEntry {
  name: string;
  is_dir: boolean;
  size: number;
  permissions: string;
}

/**
 * `base` is the sandbox resource prefix — either `/api/v1/sandbox/{workspaceId}`
 * (editor) or `/api/v1/agent-sessions/{sessionId}` (chat sessions). Both expose
 * the same file/git/terminal proxy surface, so all these helpers are keyed by it.
 */
function fileTreeKey(base: string) {
  return `${base}/file-tree`;
}

/** File tree for a sandbox, live-refreshed whenever the agent (or anything else)
 * changes a file — the fs-watch socket only tells us "something changed", so we just
 * revalidate rather than tracking its Lamport clock for precise diffing. */
export function useFileTree(base: string | null) {
  const key = base ? fileTreeKey(base) : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<FileTreeResponse>(key!), {
    revalidateOnFocus: false,
  });

  useFsWatch(base, key ? [key] : []);

  return { tree: data?.tree ?? [], workspaceRoot: data?.workspace_root ?? null, isLoading, error };
}

/** Lazily list a directory's immediate children (used to expand tree nodes marked `lazy`). */
export async function listDirectory(base: string, path: string): Promise<FileEntry[]> {
  const result = await fetchEndpointData<{ files: FileEntry[] }>(`${base}/files?path=${encodeURIComponent(path)}`);
  return result.files;
}

export function useFileContent(base: string | null, path: string | null) {
  const key = base && path ? `${base}/file?path=${encodeURIComponent(path)}` : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<{ content: string; path: string }>(key!), {
    revalidateOnFocus: false,
  });
  return { content: data?.content ?? null, isLoading, error };
}

/** One-off read of a file's content — for actions (e.g. "Add to chat") triggered outside a
 * component's render, where `useFileContent`'s hook form doesn't fit. */
export async function getFileContent(base: string, path: string): Promise<string> {
  const result = await fetchEndpointData<{ content: string; path: string }>(
    `${base}/file?path=${encodeURIComponent(path)}`,
  );
  return result.content;
}
